import { Box } from '../../render/parts'
import { Keyboard, Monitor, Mouse } from '../parts/tech'
import { DESK_HEIGHT, PanelLegs, Pedestal, PrivacyPanel, WorkTop } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

/** Each user gets a 1.4 x 0.7 top; the pair share one frame and one screen. */
const TOP_WIDTH = 1.4
const TOP_DEPTH = 0.7
/** Cable slot between the two tops — the spine and the screen live in here. */
const SPINE_GAP = 0.1
const FOOT_DEPTH = TOP_DEPTH * 2 + SPINE_GAP
/** Distance from the origin to the centre of each worktop. */
const TOP_OFFSET = (TOP_DEPTH + SPINE_GAP) / 2

/**
 * One user's equipment, laid out for the +Z side (screen towards the middle,
 * keyboard towards the outer edge). The -Z side reuses it under a 180° turn,
 * which is what makes the two positions read as mirrored.
 */
function DeskKit() {
  return (
    <group>
      <group position={[-0.08, 0, 0.24]}>
        <Monitor width={0.5} height={0.3} base={DESK_HEIGHT} />
      </group>
      <group position={[-0.08, 0, 0.52]}>
        <Keyboard base={DESK_HEIGHT} />
      </group>
      <group position={[0.24, 0, 0.54]}>
        <Mouse base={DESK_HEIGHT} />
      </group>
    </group>
  )
}

/**
 * Two-person bench: two worktops back to back on a single shared frame, split by
 * a cable spine with the accent screen sitting straight on top of it. Origin at
 * the centre of the 1.4 x 1.5 footprint, y = 0 on the floor.
 */
function DeskBench2({ accent, variant = 'screen' }: AssetContext) {
  const dressed = variant === 'dressed'
  const withScreen = variant !== 'plain'

  return (
    <group>
      {[1, -1].map((side) => (
        <group key={side} position={[0, 0, side * TOP_OFFSET]}>
          <WorkTop width={TOP_WIDTH} depth={TOP_DEPTH} />
        </group>
      ))}

      {/* Shared frame: one pair of end panels carries both tops. */}
      <PanelLegs width={TOP_WIDTH} depth={FOOT_DEPTH} />
      {/* Structural spine, sunk far enough below the tops to read as a cable slot. */}
      <Box tone="shellSoft" size={[TOP_WIDTH - 0.06, 0.11, 0.09]} position={[0, 0.625, 0]} />

      {withScreen ? <PrivacyPanel width={TOP_WIDTH} accent={accent} /> : null}

      {/* One two-drawer pedestal per side, drawer fronts facing each user. */}
      <group position={[0.38, 0, 0.42]}>
        <Pedestal depth={0.54} height={0.6} drawers={2} />
      </group>
      <group position={[-0.38, 0, -0.42]} rotation={[0, Math.PI, 0]}>
        <Pedestal depth={0.54} height={0.6} drawers={2} />
      </group>

      {dressed
        ? [0, Math.PI].map((turn) => (
            <group key={turn} rotation={[0, turn, 0]}>
              <DeskKit />
            </group>
          ))
        : null}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-bench-2',
  name: 'Bench doble',
  category: 'desks',
  footprint: [TOP_WIDTH, FOOT_DEPTH],
  height: DESK_HEIGHT + 0.41,
  surfaceHeight: DESK_HEIGHT,
  seats: 2,
  occupantSide: 'both',
  accentable: true,
  variants: [
    { id: 'screen', name: 'Con panel' },
    { id: 'plain', name: 'Sin panel' },
    { id: 'dressed', name: 'Equipado' },
  ],
  keywords: [
    'bench',
    'doble',
    'escritorio',
    'puesto',
    'compartido',
    'shared desk',
    'workstation',
    'back to back',
  ],
  Component: DeskBench2,
}
